import { useEffect, useState } from 'react';
import { AlertCircle, Calendar, CalendarClock, Car, ChevronLeft, ChevronRight, Clock, Copy, Plus, Trash2, User, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import EmptyState from '../components/ui/EmptyState';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import Select from '../components/ui/Select';
import PageHeader from '../components/shared/PageHeader';
import { usePlanFeatures } from '../hooks/usePlanFeatures';
import { supabase } from '../lib/supabase';
import { getOficinaId } from '../lib/office';
import { createServiceOrder } from '../lib/serviceOrders';
import { date } from '../utils/formatters';

const duracoes = [
  { value: '30min', label: '30 minutos' },
  { value: '1h', label: '1 hora' },
  { value: '2h', label: '2 horas' },
  { value: '3h+', label: '3+ horas' },
];
const diasSemana = [
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
  { value: 7, label: 'Dom' },
];
const defaultScheduleForm = {
  dias_funcionamento: [1, 2, 3, 4, 5, 6],
  hora_abertura: '08:00',
  hora_fechamento: '18:00',
  sabado_hora_abertura: '08:00',
  sabado_hora_fechamento: '12:00',
  intervalo_inicio: '12:00',
  intervalo_fim: '13:00',
  tempo_padrao_agendamento: 60,
};

const toTimeInput = (value, fallback) => String(value || fallback || '').slice(0, 5);
const normalizeCurrency = (value) => Number(value || 0);
const durationFromMinutes = (minutes, fallback) => {
  const value = Number(minutes || 0);
  if (value === 30) return '30min';
  if (value === 60) return '1h';
  if (value === 120) return '2h';
  if (value >= 180) return '3h+';
  return fallback;
};

export default function Agenda() {
  const { loading: planLoading, features: planFeatures } = usePlanFeatures();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [oficinaId, setOficinaId] = useState(null);
  const [oficina, setOficina] = useState(null);
  const [agendamentos, setAgendamentos] = useState([]);
  const [onlineRequests, setOnlineRequests] = useState([]);
  const [servicosCatalog, setServicosCatalog] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [mecanicos, setMecanicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDayModal, setShowDayModal] = useState(false);
  const [dayAgendamentos, setDayAgendamentos] = useState([]);
  const [reagendamento, setReagendamento] = useState(null);
  const [agendaReagendamento, setAgendaReagendamento] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [savingAgendamento, setSavingAgendamento] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [processingOnlineId, setProcessingOnlineId] = useState(null);
  const [copiedOnlineLink, setCopiedOnlineLink] = useState(false);
  const [scheduleForm, setScheduleForm] = useState(defaultScheduleForm);
  const [form, setForm] = useState({
    cliente_id: '',
    veiculo_id: '',
    data: '',
    hora: '',
    servico_id: '',
    tipo_servico: '',
    descricao: '',
    mecanico_id: '',
    duracao: '1h',
  });

  useEffect(() => {
    loadData();
    // loadData intentionally reloads only when the visible month changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const oficina_id = await getOficinaId();
      if (!oficina_id) return;
      setOficinaId(oficina_id);

      const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).toISOString().split('T')[0];
      const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).toISOString().split('T')[0];

      const [agendaRes, onlineRes, servicosRes, clientesRes, veiculosRes, mecanicosRes, oficinaRes, configRes] = await Promise.all([
        supabase.from('agendamentos').select('*, clientes(nome, telefone), veiculos(modelo, placa), usuarios(nome)').eq('oficina_id', oficina_id).gte('data', monthStart).lte('data', monthEnd).order('data', { ascending: true }),
        supabase.from('agendamentos_online').select('*').eq('oficina_id', oficina_id).order('created_at', { ascending: false }),
        supabase.from('servicos_oficina').select('id,nome,preco,custo,descricao_padrao,duracao_estimada').eq('oficina_id', oficina_id).eq('ativo', true).order('nome'),
        supabase.from('clientes').select('id, nome, telefone').eq('oficina_id', oficina_id).is('removed_at', null).order('nome'),
        supabase.from('veiculos').select('id, modelo, placa, cliente_id').eq('oficina_id', oficina_id).is('removed_at', null).order('modelo'),
        supabase.from('usuarios').select('id, nome').eq('oficina_id', oficina_id).eq('perfil', 'mecanico').eq('ativo', true).order('nome'),
        supabase.from('oficinas').select('nome,slug').eq('id', oficina_id).maybeSingle(),
        supabase.from('configuracoes_oficina').select('*').eq('oficina_id', oficina_id).maybeSingle(),
      ]);

      if (agendaRes.error) throw agendaRes.error;
      if (clientesRes.error) throw clientesRes.error;
      if (veiculosRes.error) throw veiculosRes.error;
      if (servicosRes.error) throw servicosRes.error;
      if (onlineRes.error) console.error('Erro ao carregar solicitacoes online:', onlineRes.error);
      if (mecanicosRes.error) console.error('Erro ao carregar mecanicos:', mecanicosRes.error);
      if (configRes.error) console.error('Erro ao carregar horarios online:', configRes.error);

      setAgendamentos(agendaRes.data || []);
      setOnlineRequests(onlineRes.error ? [] : onlineRes.data || []);
      setServicosCatalog(servicosRes.data || []);
      setClientes(clientesRes.data || []);
      setVeiculos(veiculosRes.data || []);
      setMecanicos(mecanicosRes.error ? [] : mecanicosRes.data || []);
      setOficina(oficinaRes.data || null);
      const config = configRes.data;
      setScheduleForm(config ? {
        dias_funcionamento: config.dias_funcionamento || defaultScheduleForm.dias_funcionamento,
        hora_abertura: toTimeInput(config.hora_abertura, defaultScheduleForm.hora_abertura),
        hora_fechamento: toTimeInput(config.hora_fechamento, defaultScheduleForm.hora_fechamento),
        sabado_hora_abertura: toTimeInput(config.sabado_hora_abertura, defaultScheduleForm.sabado_hora_abertura),
        sabado_hora_fechamento: toTimeInput(config.sabado_hora_fechamento, defaultScheduleForm.sabado_hora_fechamento),
        intervalo_inicio: toTimeInput(config.intervalo_almoco?.inicio, defaultScheduleForm.intervalo_inicio),
        intervalo_fim: toTimeInput(config.intervalo_almoco?.fim, defaultScheduleForm.intervalo_fim),
        tempo_padrao_agendamento: config.tempo_padrao_agendamento || defaultScheduleForm.tempo_padrao_agendamento,
      } : defaultScheduleForm);
    } catch (err) {
      setError(err);
      setAgendamentos([]);
      setOnlineRequests([]);
      setServicosCatalog([]);
      setClientes([]);
      setVeiculos([]);
      setMecanicos([]);
      toast.error('Erro ao carregar dados da agenda');
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    for (let i = 0; i < firstDay.getDay(); i += 1) days.push(null);
    for (let day = 1; day <= lastDay.getDate(); day += 1) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      days.push({
        day,
        dateStr,
        agendamentos: agendamentos.filter((item) => item.data === dateStr),
        isToday: dateStr === new Date().toISOString().split('T')[0],
        isSelected: dateStr === selectedDate,
      });
    }
    return days;
  };

  const navigateMonth = (direction) => {
    setCurrentDate((prev) => {
      const next = new Date(prev);
      next.setMonth(next.getMonth() + (direction === 'prev' ? -1 : 1));
      return next;
    });
  };

  const handleDayClick = (day) => {
    if (!day) return;
    setSelectedDate(day.dateStr);
    setDayAgendamentos(day.agendamentos);
    setShowDayModal(true);
  };

  const handleSaveAgendamento = async () => {
    if (savingAgendamento) return;
    if (!form.cliente_id || !form.veiculo_id || !form.data || !form.hora || !form.servico_id) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    setSavingAgendamento(true);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const selectedService = servicosCatalog.find((service) => String(service.id) === String(form.servico_id));
      const { error: insertError } = await supabase.from('agendamentos').insert({
        oficina_id: oficinaId,
        cliente_id: form.cliente_id,
        veiculo_id: form.veiculo_id,
        data: form.data,
        hora: form.hora,
        data_hora: `${form.data}T${form.hora}:00`,
        tipo_servico: selectedService?.nome || form.tipo_servico,
        descricao: form.descricao || selectedService?.descricao_padrao || '',
        mecanico_id: form.mecanico_id || null,
        duracao: form.duracao,
        status: 'agendado',
        created_by: authData?.user?.id,
      });
      if (insertError) throw insertError;
      toast.success('Agendamento criado. Confirme para gerar a OS.');
      setShowModal(false);
      setForm({ cliente_id: '', veiculo_id: '', data: '', hora: '', servico_id: '', tipo_servico: '', descricao: '', mecanico_id: '', duracao: '1h' });
      loadData();
    } catch (err) {
      toast.error(err.message || 'Erro ao criar agendamento');
    } finally {
      setSavingAgendamento(false);
    }
  };

  const handleStatusChange = async (agendamentoId, newStatus) => {
    try {
      const { error: updateError } = await supabase.from('agendamentos').update({ status: newStatus }).eq('id', agendamentoId).eq('oficina_id', oficinaId);
      if (updateError) throw updateError;
      toast.success('Status atualizado');
      loadData();
      setDayAgendamentos((items) => items.map((item) => item.id === agendamentoId ? { ...item, status: newStatus } : item));
    } catch (err) {
      toast.error(err.message || 'Erro ao atualizar status');
    }
  };

  const handleDeleteAgendamento = async () => {
    if (!deleteTarget?.id) return;
    try {
      const { error: updateOnlineError } = await supabase
        .from('agendamentos_online')
        .update({ status: 'cancelado' })
        .eq('agendamento_id', deleteTarget.id)
        .eq('oficina_id', oficinaId);
      if (updateOnlineError) console.error('Erro ao atualizar solicitacao online vinculada:', updateOnlineError);

      const { error: deleteError } = await supabase
        .from('agendamentos')
        .delete()
        .eq('id', deleteTarget.id)
        .eq('oficina_id', oficinaId);
      if (deleteError) throw deleteError;

      toast.success('Agendamento excluido da agenda');
      setAgendamentos((items) => items.filter((item) => item.id !== deleteTarget.id));
      setDayAgendamentos((items) => items.filter((item) => item.id !== deleteTarget.id));
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      toast.error(err.message || 'Erro ao excluir agendamento');
    }
  };

  const updateOnlineStatus = async (id, status, extra = {}) => {
    const { error: updateError } = await supabase.from('agendamentos_online').update({ status, ...extra }).eq('id', id).eq('oficina_id', oficinaId);
    if (updateError) throw updateError;
  };

  const getOnlineService = async (solicitacao) => {
    if (!solicitacao?.servico_id) {
      return {
        id: null,
        nome: solicitacao?.servico_nome || 'Agendamento online',
        preco: 0,
        custo: 0,
      };
    }

    const { data, error } = await supabase
      .from('servicos_oficina')
      .select('id,nome,preco,custo,descricao_padrao')
      .eq('id', solicitacao.servico_id)
      .eq('oficina_id', oficinaId)
      .maybeSingle();

    if (error) throw error;

    return {
      id: data?.id || solicitacao.servico_id,
      nome: data?.descricao_padrao || data?.nome || solicitacao?.servico_nome || 'Agendamento online',
      preco: normalizeCurrency(data?.preco),
      custo: normalizeCurrency(data?.custo),
    };
  };

  const applyOnlineServiceToOS = async (osId, solicitacao) => {
    if (!osId) return;
    const service = await getOnlineService(solicitacao);
    let existingQuery = supabase
      .from('os_servicos')
      .select('id')
      .eq('os_id', osId);

    existingQuery = service.id
      ? existingQuery.eq('servico_id', service.id)
      : existingQuery.eq('descricao', service.nome);

    const { data: existing, error: existingError } = await existingQuery.maybeSingle();

    if (existingError) throw existingError;

    if (!existing) {
      const { error: serviceError } = await supabase.from('os_servicos').insert({
        os_id: osId,
        servico_id: service.id,
        descricao: service.nome,
        valor: service.preco,
        custo: service.custo,
      });
      if (serviceError) throw serviceError;
    }

    const { error: osUpdateError } = await supabase
      .from('ordens_servico')
      .update({
        valor_total: service.preco,
        custo_total: service.custo,
        lucro_bruto: service.preco - service.custo,
      })
      .eq('id', osId)
      .eq('oficina_id', oficinaId);
    if (osUpdateError) throw osUpdateError;
  };

  const getAgendaService = (agendamento) => {
    const serviceName = String(agendamento?.tipo_servico || '').trim().toLowerCase();
    const service = servicosCatalog.find((item) => String(item.nome || '').trim().toLowerCase() === serviceName);

    return {
      id: service?.id || null,
      nome: service?.descricao_padrao || service?.nome || agendamento?.tipo_servico || 'Agendamento',
      preco: normalizeCurrency(service?.preco),
      custo: normalizeCurrency(service?.custo),
    };
  };

  const findOrderByAppointment = async (agendamentoId) => {
    if (!agendamentoId) return null;
    const { data, error } = await supabase
      .from('ordens_servico')
      .select('id')
      .eq('agendamento_id', agendamentoId)
      .eq('oficina_id', oficinaId)
      .maybeSingle();
    if (error) throw error;
    return data || null;
  };

  const linkOrderToAppointment = async (osId, agendamentoId) => {
    if (!osId || !agendamentoId) return;
    const { error } = await supabase
      .from('ordens_servico')
      .update({ agendamento_id: agendamentoId })
      .eq('id', osId)
      .eq('oficina_id', oficinaId);
    if (error) throw error;
  };

  const createOrderFromAgendaService = async ({ clienteId, veiculoId, mecanicoId, problema, service }) => {
    return createServiceOrder({
      cliente_id: clienteId,
      veiculo_id: veiculoId,
      mecanico_id: mecanicoId || null,
      problema_relatorio: problema,
      observacoes: problema,
      servicos: [{
        servico_id: service.id || null,
        descricao: service.nome,
        valor: service.preco,
        custo: service.custo,
      }],
      pecas: [],
    });
  };

  const salvarAgendaReagendamento = async () => {
    if (!agendaReagendamento?.data || !agendaReagendamento?.hora) {
      toast.error('Informe a nova data e horário');
      return;
    }

    try {
      const nextTime = String(agendaReagendamento.hora).slice(0, 5);
      const { error: updateError } = await supabase
        .from('agendamentos')
        .update({
          data: agendaReagendamento.data,
          hora: nextTime,
          data_hora: `${agendaReagendamento.data}T${nextTime}:00`,
          status: agendaReagendamento.status === 'convertido' ? 'agendado' : agendaReagendamento.status,
        })
        .eq('id', agendaReagendamento.id)
        .eq('oficina_id', oficinaId);
      if (updateError) throw updateError;

      toast.success('Agendamento reagendado');
      setAgendaReagendamento(null);
      loadData();
    } catch (err) {
      toast.error(err.message || 'Erro ao reagendar agendamento');
    }
  };

  const aprovarOnline = async (solicitacao) => {
    if (processingOnlineId) return;
    setProcessingOnlineId(solicitacao.id);
    try {
      if (solicitacao.os_id) {
        await applyOnlineServiceToOS(solicitacao.os_id, solicitacao);
        await updateOnlineStatus(solicitacao.id, 'confirmado');
        toast.success('Solicitacao ja possui OS vinculada');
        loadData();
        return;
      }
      const { data: agendamento, error: insertError } = await supabase.from('agendamentos').insert({
        oficina_id: oficinaId,
        cliente_id: solicitacao.cliente_id,
        veiculo_id: solicitacao.veiculo_id,
        data: solicitacao.data_agendamento,
        hora: solicitacao.hora_agendamento,
        data_hora: `${solicitacao.data_agendamento}T${String(solicitacao.hora_agendamento).slice(0, 5)}:00`,
        tipo_servico: solicitacao.servico_nome || 'Agendamento online',
        descricao: solicitacao.problema_relato,
        duracao: `${solicitacao.duracao_estimada || 60}min`,
        status: 'confirmado',
      }).select('id').single();
      if (insertError) throw insertError;
      const { data: os } = await supabase
        .from('ordens_servico')
        .select('id')
        .eq('agendamento_id', agendamento.id)
        .eq('oficina_id', oficinaId)
        .maybeSingle();
      await applyOnlineServiceToOS(os?.id, solicitacao);
      await updateOnlineStatus(solicitacao.id, 'confirmado', { agendamento_id: agendamento.id, os_id: os?.id || null });
      toast.success('Solicitação aprovada e transformada automaticamente em OS');
      loadData();
    } catch (err) {
      toast.error(err.message || 'Erro ao aprovar solicitação');
    }
    finally {
      setProcessingOnlineId(null);
    }
  };

  const cancelarOnline = async (solicitacao) => {
    if (processingOnlineId) return;
    setProcessingOnlineId(solicitacao.id);
    try {
      await updateOnlineStatus(solicitacao.id, 'cancelado');
      toast.success('Solicitação cancelada');
      loadData();
    } catch (err) {
      toast.error(err.message || 'Erro ao cancelar solicitação');
    }
    finally {
      setProcessingOnlineId(null);
    }
  };

  const salvarReagendamento = async () => {
    if (!reagendamento?.data_agendamento || !reagendamento?.hora_agendamento) {
      toast.error('Informe a nova data e horário');
      return;
    }
    try {
      await updateOnlineStatus(reagendamento.id, 'reagendamento', {
        data_agendamento: reagendamento.data_agendamento,
        hora_agendamento: reagendamento.hora_agendamento,
      });
      toast.success('Reagendamento registrado');
      setReagendamento(null);
      loadData();
    } catch (err) {
      toast.error(err.message || 'Erro ao reagendar');
    }
  };

  const converterOnlineEmOS = async (solicitacao) => {
    if (processingOnlineId) return;
    setProcessingOnlineId(solicitacao.id);
    try {
      if (solicitacao.os_id) {
        await applyOnlineServiceToOS(solicitacao.os_id, solicitacao);
        await updateOnlineStatus(solicitacao.id, 'confirmado');
        toast.success('Solicitacao ja possui OS vinculada');
        loadData();
        return;
      }
      const service = await getOnlineService(solicitacao);
      const os = await createOrderFromAgendaService({
        clienteId: solicitacao.cliente_id,
        veiculoId: solicitacao.veiculo_id,
        mecanicoId: null,
        problema: solicitacao.problema_relato || `Convertido do agendamento online: ${solicitacao.servico_nome || ''}`,
        service,
      });
      await updateOnlineStatus(solicitacao.id, 'confirmado', { os_id: os.id });
      toast.success('Solicitação convertida em OS');
      loadData();
    } catch (err) {
      toast.error(err.message || 'Erro ao converter em OS');
    }
    finally {
      setProcessingOnlineId(null);
    }
  };

  const handleConverterEmOS = async (agendamento) => {
    try {
      const existingOrder = await findOrderByAppointment(agendamento.id);
      if (existingOrder?.id) {
        await handleStatusChange(agendamento.id, 'convertido');
        toast.success('Agendamento ja possui OS vinculada.');
        return;
      }

      const service = getAgendaService(agendamento);
      const os = await createOrderFromAgendaService({
        clienteId: agendamento.cliente_id,
        veiculoId: agendamento.veiculo_id,
        mecanicoId: agendamento.mecanico_id,
        problema: `Convertido do agendamento: ${agendamento.descricao || agendamento.tipo_servico}`,
        service,
      });

      await linkOrderToAppointment(os.id, agendamento.id);
      await handleStatusChange(agendamento.id, 'convertido');
      toast.success('Agendamento convertido em OS com sucesso!');
    } catch (err) {
      toast.error(err.message || 'Erro ao converter em OS');
    }
  };

  const getStatusColor = (status) => {
    if (status === 'confirmado') return 'bg-green-100 text-green-800';
    if (status === 'cancelado') return 'bg-red-100 text-red-800';
    if (status === 'convertido') return 'bg-purple-100 text-purple-800';
    return 'bg-blue-100 text-blue-800';
  };

  const updateScheduleField = (field, value) => {
    setScheduleForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleScheduleDay = (day) => {
    setScheduleForm((prev) => {
      const selected = prev.dias_funcionamento.includes(day)
        ? prev.dias_funcionamento.filter((item) => item !== day)
        : [...prev.dias_funcionamento, day].sort((a, b) => a - b);
      return { ...prev, dias_funcionamento: selected };
    });
  };

  const salvarHorariosOnline = async () => {
    if (savingSchedule) return;
    if (scheduleForm.dias_funcionamento.length === 0) {
      toast.error('Selecione pelo menos um dia para o agendamento online');
      return;
    }
    if (scheduleForm.hora_abertura >= scheduleForm.hora_fechamento) {
      toast.error('O horário de abertura precisa ser menor que o fechamento');
      return;
    }
    if (scheduleForm.dias_funcionamento.includes(6) && scheduleForm.sabado_hora_abertura >= scheduleForm.sabado_hora_fechamento) {
      toast.error('O horário de sábado precisa ser válido');
      return;
    }

    setSavingSchedule(true);
    try {
      const payload = {
        oficina_id: oficinaId,
        dias_funcionamento: scheduleForm.dias_funcionamento,
        hora_abertura: scheduleForm.hora_abertura,
        hora_fechamento: scheduleForm.hora_fechamento,
        sabado_hora_abertura: scheduleForm.sabado_hora_abertura || null,
        sabado_hora_fechamento: scheduleForm.sabado_hora_fechamento || null,
        intervalo_almoco: {
          inicio: scheduleForm.intervalo_inicio || '',
          fim: scheduleForm.intervalo_fim || '',
        },
        tempo_padrao_agendamento: Number(scheduleForm.tempo_padrao_agendamento) || 60,
      };
      const { error: saveError } = await supabase
        .from('configuracoes_oficina')
        .upsert(payload, { onConflict: 'oficina_id' });
      if (saveError) throw saveError;
      toast.success('Horários do agendamento online salvos');
      loadData();
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar horários online');
    } finally {
      setSavingSchedule(false);
    }
  };

  const filteredVeiculos = veiculos.filter((item) => item.cliente_id === form.cliente_id);
  const onlineEnabled = Boolean(planFeatures.onlineScheduling);
  const onlinePendentes = onlineRequests.filter((item) => ['pendente', 'reagendamento'].includes(item.status));
  const onlineLink = oficina?.slug ? `${window.location.origin}/agendar/${oficina.slug}` : '';
  const copyOnlineLink = async () => {
    if (!onlineLink) return;
    try {
      await navigator.clipboard.writeText(onlineLink);
      setCopiedOnlineLink(true);
      setTimeout(() => setCopiedOnlineLink(false), 1800);
      toast.success('Link de agendamento copiado com sucesso');
    } catch (copyError) {
      toast.error('Não foi possível copiar o link automaticamente');
    }
  };

  if (loading || planLoading) return <div className="flex h-64 items-center justify-center text-zinc-400">Carregando agenda...</div>;

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Agenda" subtitle="Calendário de agendamentos da oficina" />
        <Card>
          <EmptyState icon={AlertCircle} title="Erro ao carregar agenda" description={error?.message || 'Não foi possível carregar os dados da agenda.'} action={loadData} actionText="Tentar novamente" />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Agenda" subtitle="Calendário de agendamentos da oficina" actions={<Button onClick={() => setShowModal(true)}><Plus size={16} /> Novo Agendamento</Button>} />

      {onlineEnabled ? (
        <Card>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold">Solicitações online</h3>
              <p className="text-sm text-zinc-400">Pedidos feitos pelo link público único da oficina</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {onlineLink && (
                <Button variant="ghost" onClick={copyOnlineLink}><Copy size={15} /> {copiedOnlineLink ? 'Copiado' : 'Copiar link'}</Button>
              )}
              <Badge>{onlinePendentes.length > 0 ? `${onlinePendentes.length} nova solicitação` : 'sem pendências'}</Badge>
            </div>
          </div>
          <div className="mb-5 rounded-xl border border-white/10 bg-surface2 p-4">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className="flex items-center gap-2 font-semibold"><Clock size={17} /> Horários do agendamento online</h4>
                <p className="text-sm text-zinc-400">Defina quando a oficina aparece disponível no link público.</p>
              </div>
              <Button size="sm" disabled={savingSchedule} onClick={salvarHorariosOnline}>{savingSchedule ? 'Salvando...' : 'Salvar horários'}</Button>
            </div>
            <div className="mb-4 flex flex-wrap gap-2">
              {diasSemana.map((day) => {
                const active = scheduleForm.dias_funcionamento.includes(day.value);
                return (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => toggleScheduleDay(day.value)}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${active ? 'border-orange-400 bg-orange-400 text-zinc-950 shadow-sm' : 'border-white/10 bg-surface text-zinc-400 hover:text-zinc-200'}`}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>
            <div className="grid gap-3 md:grid-cols-4">
              <div><label className="mb-1 block text-sm font-medium">Abertura padrão</label><Input type="time" value={scheduleForm.hora_abertura} onChange={(event) => updateScheduleField('hora_abertura', event.target.value)} /></div>
              <div><label className="mb-1 block text-sm font-medium">Fechamento padrão</label><Input type="time" value={scheduleForm.hora_fechamento} onChange={(event) => updateScheduleField('hora_fechamento', event.target.value)} /></div>
              <div><label className="mb-1 block text-sm font-medium">Início do almoço</label><Input type="time" value={scheduleForm.intervalo_inicio} onChange={(event) => updateScheduleField('intervalo_inicio', event.target.value)} /></div>
              <div><label className="mb-1 block text-sm font-medium">Fim do almoço</label><Input type="time" value={scheduleForm.intervalo_fim} onChange={(event) => updateScheduleField('intervalo_fim', event.target.value)} /></div>
              <div><label className="mb-1 block text-sm font-medium">Abertura sábado</label><Input type="time" value={scheduleForm.sabado_hora_abertura} onChange={(event) => updateScheduleField('sabado_hora_abertura', event.target.value)} disabled={!scheduleForm.dias_funcionamento.includes(6)} /></div>
              <div><label className="mb-1 block text-sm font-medium">Fechamento sábado</label><Input type="time" value={scheduleForm.sabado_hora_fechamento} onChange={(event) => updateScheduleField('sabado_hora_fechamento', event.target.value)} disabled={!scheduleForm.dias_funcionamento.includes(6)} /></div>
              <div className="md:col-span-2"><label className="mb-1 block text-sm font-medium">Intervalo entre horários</label><Select value={String(scheduleForm.tempo_padrao_agendamento)} onChange={(event) => updateScheduleField('tempo_padrao_agendamento', Number(event.target.value))} options={[15, 30, 45, 60, 90, 120].map((item) => ({ value: String(item), label: `${item} minutos` }))} /></div>
            </div>
          </div>
          {onlineRequests.length === 0 ? (
            <EmptyState icon={Calendar} title="Nenhuma solicitação online" description="Novos pedidos aparecerão aqui." />
          ) : (
            <div className="space-y-2">
              {onlineRequests.slice(0, 8).map((item) => (
                <div key={item.id} className="rounded-lg border border-border bg-surface2 p-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap gap-2">
                        {item.status === 'pendente' && <Badge>Nova solicitação</Badge>}
                        <Badge>{item.status}</Badge>
                      </div>
                      <p className="mt-2 font-semibold">{item.nome_cliente}</p>
                      <p className="text-sm text-zinc-400">{item.veiculo} • {item.placa}</p>
                      <p className="text-sm text-zinc-400">{date(item.data_agendamento)} às {String(item.hora_agendamento).slice(0, 5)} • {item.servico_nome}</p>
                      {item.problema_relato && <p className="mt-1 text-sm text-zinc-300">{item.problema_relato}</p>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {['pendente', 'reagendamento'].includes(item.status) && <Button size="sm" disabled={processingOnlineId === item.id} onClick={() => aprovarOnline(item)}>{processingOnlineId === item.id ? 'Processando...' : 'Aceitar'}</Button>}
                      {['pendente', 'reagendamento'].includes(item.status) && <Button size="sm" variant="ghost" disabled={processingOnlineId === item.id} onClick={() => setReagendamento(item)}>Reagendar</Button>}
                      {item.status !== 'cancelado' && <Button size="sm" variant="danger" disabled={processingOnlineId === item.id} onClick={() => cancelarOnline(item)}>Recusar</Button>}
                      {item.status === 'confirmado' && !item.os_id && <Button size="sm" variant="ghost" disabled={processingOnlineId === item.id} onClick={() => converterOnlineEmOS(item)}>Converter em OS</Button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      ) : (
        <Card className="border-orange-500/30 bg-orange-500/10">
          <p className="text-sm text-orange-200">Agendamento online por link público disponível nos planos Pro e Premium.</p>
        </Card>
      )}

      <Card>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">{date(currentDate, { month: 'long', year: 'numeric' })}</h3>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigateMonth('prev')}><ChevronLeft size={16} /></Button>
            <Button variant="ghost" size="sm" onClick={() => navigateMonth('next')}><ChevronRight size={16} /></Button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((day) => <div key={day} className="p-2 text-center text-sm font-medium text-zinc-500">{day}</div>)}
          {getDaysInMonth().map((day, index) => (
            <div key={index} className="aspect-square">
              {day ? (
                <button onClick={() => handleDayClick(day)} className={`h-full w-full rounded-lg border p-2 text-left transition ${day.isToday ? 'border-accent bg-accent/10' : day.isSelected ? 'border-accent bg-accent/5' : 'border-border hover:bg-surface2'}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${day.isToday ? 'font-bold text-accent' : ''}`}>{day.day}</span>
                    {day.agendamentos.length > 0 && <span className="rounded-full bg-accent px-1.5 text-xs text-white">{day.agendamentos.length}</span>}
                  </div>
                  <div className="mt-1 space-y-1">
                    {day.agendamentos.slice(0, 2).map((ag) => <div key={ag.id} className="truncate rounded bg-blue-100 px-1 py-0.5 text-[10px] text-blue-800">{String(ag.hora).slice(0, 5)} - {ag.clientes?.nome?.split(' ')[0]}</div>)}
                  </div>
                </button>
              ) : <div className="h-full w-full" />}
            </div>
          ))}
        </div>
      </Card>

      <Modal open={showModal} onClose={() => setShowModal(false)} title="Novo Agendamento">
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div><label className="mb-1 block text-sm font-medium">Cliente *</label><Select value={form.cliente_id} onChange={(event) => setForm((prev) => ({ ...prev, cliente_id: event.target.value, veiculo_id: '' }))}>{<><option value="">Selecione um cliente</option>{clientes.map((cliente) => <option key={cliente.id} value={cliente.id}>{cliente.nome}</option>)}</>}</Select></div>
            <div><label className="mb-1 block text-sm font-medium">Veículo *</label><Select value={form.veiculo_id} onChange={(event) => setForm((prev) => ({ ...prev, veiculo_id: event.target.value }))} disabled={!form.cliente_id}>{<><option value="">Selecione um veículo</option>{filteredVeiculos.map((veiculo) => <option key={veiculo.id} value={veiculo.id}>{veiculo.modelo} - {veiculo.placa}</option>)}</>}</Select></div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div><label className="mb-1 block text-sm font-medium">Data *</label><Input type="date" value={form.data} onChange={(event) => setForm((prev) => ({ ...prev, data: event.target.value }))} /></div>
            <div><label className="mb-1 block text-sm font-medium">Horário *</label><Input type="time" value={form.hora} onChange={(event) => setForm((prev) => ({ ...prev, hora: event.target.value }))} /></div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium">Tipo de serviço *</label>
              <Select
                value={form.servico_id}
                onChange={(event) => {
                  const selectedService = servicosCatalog.find((service) => String(service.id) === String(event.target.value));
                  setForm((prev) => ({
                    ...prev,
                    servico_id: event.target.value,
                    tipo_servico: selectedService?.nome || '',
                    descricao: prev.descricao || selectedService?.descricao_padrao || '',
                    duracao: durationFromMinutes(selectedService?.duracao_estimada, prev.duracao),
                  }));
                }}
              >
                {<><option value="">Selecione</option>{servicosCatalog.map((servico) => <option key={servico.id} value={servico.id}>{servico.nome}</option>)}</>}
              </Select>
              {servicosCatalog.length === 0 && <p className="mt-1 text-xs text-zinc-500">Nenhum serviço ativo cadastrado.</p>}
            </div>
            <div><label className="mb-1 block text-sm font-medium">Duração</label><Select value={form.duracao} onChange={(event) => setForm((prev) => ({ ...prev, duracao: event.target.value }))} options={duracoes} /></div>
          </div>
          <div><label className="mb-1 block text-sm font-medium">Mecânico</label><Select value={form.mecanico_id} onChange={(event) => setForm((prev) => ({ ...prev, mecanico_id: event.target.value }))}>{<><option value="">Sem mecânico definido</option>{mecanicos.map((mecanico) => <option key={mecanico.id} value={mecanico.id}>{mecanico.nome}</option>)}</>}</Select></div>
          <div><label className="mb-1 block text-sm font-medium">Descrição</label><textarea className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none" rows={3} value={form.descricao} onChange={(event) => setForm((prev) => ({ ...prev, descricao: event.target.value }))} /></div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end"><Button variant="ghost" disabled={savingAgendamento} onClick={() => setShowModal(false)}>Cancelar</Button><Button disabled={savingAgendamento} onClick={handleSaveAgendamento}>{savingAgendamento ? 'Criando agendamento...' : 'Confirmar'}</Button></div>
        </div>
      </Modal>

      <Modal open={showDayModal} onClose={() => setShowDayModal(false)} title={`Agendamentos - ${date(selectedDate)}`}>
        <div className="space-y-3">
          {dayAgendamentos.length === 0 ? <div className="py-8 text-center text-zinc-400"><Calendar size={48} className="mx-auto mb-2 opacity-50" /><p>Nenhum agendamento para este dia</p></div> : dayAgendamentos.map((agendamento) => (
            <Card key={agendamento.id} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="mb-2 flex items-center gap-3"><span className="font-semibold">{String(agendamento.hora).slice(0, 5)}</span><Badge className={getStatusColor(agendamento.status)}>{agendamento.status}</Badge></div>
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2"><User size={14} /><span>{agendamento.clientes?.nome}</span></div>
                    <div className="flex items-center gap-2"><Car size={14} /><span>{agendamento.veiculos?.modelo} - {agendamento.veiculos?.placa}</span></div>
                    <p className="text-zinc-400">{agendamento.tipo_servico || 'Serviço não informado'}</p>
                    {agendamento.descricao && <p className="mt-2 text-zinc-400">{agendamento.descricao}</p>}
                  </div>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  {agendamento.status !== 'convertido' && agendamento.status !== 'cancelado' && (
                    <Button size="sm" variant="ghost" onClick={() => setAgendaReagendamento(agendamento)} title="Reagendar">
                      <CalendarClock size={14} />
                    </Button>
                  )}
                  {agendamento.status !== 'convertido' && agendamento.status !== 'cancelado' && <Button size="sm" onClick={() => handleConverterEmOS(agendamento)}>Converter em OS</Button>}
                  {agendamento.status !== 'cancelado' && agendamento.status !== 'convertido' && <Button size="sm" variant="ghost" onClick={() => handleStatusChange(agendamento.id, 'cancelado')}><X size={14} /></Button>}
                  {agendamento.status !== 'convertido' && (
                    <Button size="sm" variant="danger" onClick={() => setDeleteTarget(agendamento)} title="Excluir agendamento">
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </Modal>

      <Modal open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} title="Excluir agendamento">
        <div className="space-y-4">
          <p className="text-sm text-zinc-300">
            Confirma a exclusão definitiva do agendamento de {deleteTarget?.clientes?.nome || 'cliente'} em {deleteTarget?.data ? date(deleteTarget.data) : 'data não informada'} às {String(deleteTarget?.hora || '').slice(0, 5)}?
          </p>
          <p className="text-xs text-zinc-500">Use esta opção quando o cliente cancelar e o horário precisar sair da agenda.</p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Voltar</Button>
            <Button variant="danger" onClick={handleDeleteAgendamento}>Excluir definitivamente</Button>
          </div>
        </div>
      </Modal>

      <Modal open={Boolean(agendaReagendamento)} onClose={() => setAgendaReagendamento(null)} title="Reagendar agendamento">
        <div className="space-y-4">
          <p className="text-sm text-zinc-400">
            {agendaReagendamento?.clientes?.nome || 'Cliente'} • {agendaReagendamento?.tipo_servico || 'Serviço'}
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <div><label className="mb-1 block text-sm font-medium">Nova data</label><Input type="date" value={agendaReagendamento?.data || ''} onChange={(event) => setAgendaReagendamento((prev) => ({ ...prev, data: event.target.value }))} /></div>
            <div><label className="mb-1 block text-sm font-medium">Novo horário</label><Input type="time" value={String(agendaReagendamento?.hora || '').slice(0, 5)} onChange={(event) => setAgendaReagendamento((prev) => ({ ...prev, hora: event.target.value }))} /></div>
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end"><Button variant="ghost" onClick={() => setAgendaReagendamento(null)}>Cancelar</Button><Button onClick={salvarAgendaReagendamento}>Salvar</Button></div>
        </div>
      </Modal>

      <Modal open={Boolean(reagendamento)} onClose={() => setReagendamento(null)} title="Reagendar solicitação">
        <div className="space-y-4">
          <p className="text-sm text-zinc-400">{reagendamento?.nome_cliente} • {reagendamento?.servico_nome}</p>
          <div className="grid gap-3 md:grid-cols-2">
            <div><label className="mb-1 block text-sm font-medium">Nova data</label><Input type="date" value={reagendamento?.data_agendamento || ''} onChange={(event) => setReagendamento((prev) => ({ ...prev, data_agendamento: event.target.value }))} /></div>
            <div><label className="mb-1 block text-sm font-medium">Novo horário</label><Input type="time" value={String(reagendamento?.hora_agendamento || '').slice(0, 5)} onChange={(event) => setReagendamento((prev) => ({ ...prev, hora_agendamento: event.target.value }))} /></div>
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end"><Button variant="ghost" onClick={() => setReagendamento(null)}>Cancelar</Button><Button onClick={salvarReagendamento}>Salvar</Button></div>
        </div>
      </Modal>
    </div>
  );
}
